import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, Lightbulb, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../../config';

export const EscenarioPersonalizado: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map((s) => s.trim()).filter(Boolean).filter((s) => s !== '0'),
    [idsParam],
  );

  const escenarioDescParam = params.get('escenario_desc') || '';

  const [prompt, setPrompt] = useState<string>(escenarioDescParam);
  const [ideas, setIdeas] = useState<string[]>([]);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const canSubmit = prompt.trim().length > 0;

  const handleGenerateIdeas = async () => {
    if (ideasLoading) return;
    try {
      setIdeasLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('No se pudo identificar al usuario');
        return;
      }

      const variantIds = ids.filter(Boolean);
      if (variantIds.length === 0) {
        alert('No hay productos seleccionados para generar ideas.');
        return;
      }

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/generar-ideas-de-fotos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({
          user_id: user.id,
          variante_id: variantIds,
        }),
      });

      if (!response.ok) {
        throw new Error('Error al generar ideas');
      }

      const data = await response.json();
      if (Array.isArray(data?.ideas)) {
        setIdeas((prev) => [...prev, ...data.ideas]);
      }
    } catch (e) {
      console.error(e);
      alert('Se produjo un error al generar ideas. Por favor, volvé a intentarlo.');
    } finally {
      setIdeasLoading(false);
    }
  };

  const handleSubmit = () => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    const qp = new URLSearchParams(location.search);
    qp.set('escenario_desc', trimmed);
    navigate(`/redes-sociales/crear-anuncio/prueba-nueva-interfaz?${qp.toString()}`);
  };

  return (
    <div className="p-4 sm:p-8 pb-56 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent">
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">
        {/* Mobile header */}
        <div className="sm:hidden mt-2 mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 text-gray-700 shadow-sm"
            aria-label="Atrás"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-sm font-semibold text-gray-900">Escena</h1>
          <div className="w-10 h-10" aria-hidden="true" />
        </div>

        {/* Desktop header */}
        <div className="hidden sm:flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white border border-gray-200 text-gray-700 shadow-sm"
            aria-label="Atrás"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Escena</h1>
          <div className="w-11 h-11" aria-hidden="true" />
        </div>

        {ideas.length > 0 && (
          <div className="mt-2">
            <p className="px-4 text-sm font-medium text-gray-700">Ideas generadas por la IA</p>
            <p className="mt-1 px-4 text-sm text-gray-600">
              Seleccioná una de las ideas generadas por la IA para tu escena.
            </p>
            <div className="mt-3 bg-white border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-200 sm:max-h-60 sm:overflow-y-auto">
              {ideas.map((idea, index) => (
                <button
                  key={`${index}-${idea}`}
                  type="button"
                  onClick={() => setPrompt(idea)}
                  className="w-full flex items-start gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50"
                >
                  <div className="shrink-0 bg-blue-50 rounded-md p-1.5">
                    <Lightbulb className="w-4 h-4 text-blue-600" />
                  </div>
                  <p className="text-gray-800">{idea}</p>
                </button>
              ))}
            </div>
            <div className="mt-4 mb-2 flex justify-center">
              <button
                type="button"
                onClick={handleGenerateIdeas}
                disabled={ideasLoading}
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 bg-transparent disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                <span>Generar más ideas</span>
              </button>
            </div>
          </div>
        )}

        <div className="mt-6">
          <div className="fixed inset-x-0 bottom-0 z-30 bg-white p-3 sm:static sm:inset-auto sm:z-auto sm:bg-transparent sm:border-none sm:p-0">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-4 flex flex-col gap-3">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describí la escena que querés para tus imágenes"
                  className="w-full text-sm text-gray-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[96px]"
                />
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={handleGenerateIdeas}
                    disabled={ideasLoading}
                    className="inline-flex items-center gap-2 px-3 h-9 rounded-full border border-gray-200 bg-white text-gray-900 text-sm hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Lightbulb className="w-4 h-4 text-blue-600" />
                    <span>Generar ideas</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className={`inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-full shadow-sm ${
                      canSubmit ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                    aria-label="Listo"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EscenarioPersonalizado;
